import { FormEvent, MouseEvent, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Close } from '@mui/icons-material';
import { TextField } from '@mui/material';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { AxiosError } from 'axios';
import api from 'api';
import { AdminUsers } from 'api/models/response/AdminUsers';
import { UserProfileUpdatePayload } from 'api/rest/admin';
import { RootState } from 'redux/store';
import * as S from 'views/CalendarReminderPage/CalendarReminderPage.styled';
import * as L from './EditUserProfileModal.styled';

export type EditableUserSummary = Pick<
    AdminUsers,
    'id' | 'firstname' | 'lastname' | 'email' | 'external_id'
>;

type Props = {
    user: EditableUserSummary | null;
    open: boolean;
    onClose: () => void;
    onSaved: () => void;
};

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const EditUserProfileModal = ({ user, open, onClose, onSaved }: Props) => {
    const { t } = useTranslation();
    const { language_setting: language } = useSelector((state: RootState) => state.user.entity);

    const [firstname, setFirstname] = useState('');
    const [lastname, setLastname] = useState('');
    const [email, setEmail] = useState('');
    const [externalId, setExternalId] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [notification, setNotification] = useState<{
        variant: 'success' | 'error';
        message: string;
    } | null>(null);

    useEffect(() => {
        if (open && user) {
            setFirstname(user.firstname ?? '');
            setLastname(user.lastname ?? '');
            setEmail(user.email ?? '');
            setExternalId(user.external_id ?? '');
            setNotification(null);
        }
    }, [open, user]);

    useEffect(() => {
        if (!open) return;
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [open, onClose]);

    if (!open || !user) return null;

    const trimmedFirstname = firstname.trim();
    const trimmedLastname = lastname.trim();
    const trimmedEmail = email.trim();
    const trimmedExternalId = externalId.trim();
    const currentExternalId = (user.external_id ?? '').trim();

    const emailValid = trimmedEmail === '' || EMAIL_REGEX.test(trimmedEmail);
    const hasChanges =
        trimmedFirstname !== (user.firstname ?? '').trim() ||
        trimmedLastname !== (user.lastname ?? '').trim() ||
        trimmedEmail !== (user.email ?? '').trim() ||
        trimmedExternalId !== currentExternalId;

    const handleBackdropClick = (event: MouseEvent<HTMLDivElement>) => {
        if (event.target === event.currentTarget) onClose();
    };

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (!emailValid || !hasChanges || isSaving) return;

        const payload: UserProfileUpdatePayload = {};
        if (trimmedFirstname !== (user.firstname ?? '').trim()) {
            payload.firstname = trimmedFirstname;
        }
        if (trimmedLastname !== (user.lastname ?? '').trim()) {
            payload.lastname = trimmedLastname;
        }
        if (trimmedEmail !== (user.email ?? '').trim()) {
            payload.email = trimmedEmail;
        }
        if (trimmedExternalId !== currentExternalId) {
            payload.external_id = trimmedExternalId;
        }

        setIsSaving(true);
        try {
            await api.admin.updateUserProfile(user.id, payload);
            setNotification({
                variant: 'success',
                message: t('profileUpdatedSuccessfully', {
                    lng: language,
                    defaultValue: 'Profile updated successfully.',
                }),
            });
            onSaved();
            setTimeout(() => {
                onClose();
            }, 900);
        } catch (error) {
            const axiosError = error as AxiosError<{ msg?: string }>;
            let message = t('somethingWentWrong', { lng: language });
            if (axiosError?.response?.status === 409) {
                const serverMsg = axiosError?.response?.data?.msg ?? '';
                if (serverMsg.toLowerCase().includes('external')) {
                    message = t('externalIdAlreadyInUse', {
                        lng: language,
                        defaultValue: 'External ID already in use.',
                    });
                } else {
                    message = t('emailAlreadyInUse', {
                        lng: language,
                        defaultValue: 'Email already in use.',
                    });
                }
            } else if (axiosError?.response?.status === 403) {
                message = t('forbidden', {
                    lng: language,
                    defaultValue: 'You are not allowed to edit this user.',
                });
            } else if (axiosError?.response?.data?.msg) {
                message = axiosError.response.data.msg;
            }
            setNotification({ variant: 'error', message });
        } finally {
            setIsSaving(false);
        }
    };

    return createPortal(
        <L.Backdrop onMouseDown={handleBackdropClick}>
            <L.ModalShell>
                <S.CloseButton type="button" onClick={onClose} aria-label="Close edit form">
                    <Close />
                </S.CloseButton>

                <S.Form onSubmit={handleSubmit}>
                    <S.Header>
                        <S.Eyebrow>
                            {t('editUser', { lng: language, defaultValue: 'Edit user' })}
                        </S.Eyebrow>
                        <S.Title>
                            {t('editUserProfile', {
                                lng: language,
                                defaultValue: 'Edit user profile',
                            })}
                        </S.Title>
                        <S.Subtitle>
                            {t('editUserProfileSubtitle', {
                                lng: language,
                                defaultValue:
                                    'Update the first name, last name, email, or external ID for this user.',
                            })}
                        </S.Subtitle>
                    </S.Header>

                    <S.Section>
                        <S.Row>
                            <S.FieldGroup>
                                <TextField
                                    fullWidth
                                    label={t('firstName', {
                                        lng: language,
                                        defaultValue: 'First name',
                                    })}
                                    value={firstname}
                                    onChange={(e) => setFirstname(e.target.value)}
                                    disabled={isSaving}
                                />
                            </S.FieldGroup>
                            <S.FieldGroup>
                                <TextField
                                    fullWidth
                                    label={t('lastName', {
                                        lng: language,
                                        defaultValue: 'Last name',
                                    })}
                                    value={lastname}
                                    onChange={(e) => setLastname(e.target.value)}
                                    disabled={isSaving}
                                />
                            </S.FieldGroup>
                        </S.Row>
                        <S.FieldGroup>
                            <TextField
                                fullWidth
                                type="email"
                                label={t('email', { lng: language, defaultValue: 'Email' })}
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                error={!emailValid}
                                helperText={
                                    !emailValid
                                        ? t('invalidEmail', {
                                              lng: language,
                                              defaultValue: 'Please enter a valid email address.',
                                          })
                                        : ' '
                                }
                                disabled={isSaving}
                            />
                        </S.FieldGroup>
                        <S.FieldGroup>
                            <TextField
                                fullWidth
                                label={t('externalId', {
                                    lng: language,
                                    defaultValue: 'External ID',
                                })}
                                value={externalId}
                                onChange={(e) => setExternalId(e.target.value)}
                                disabled={isSaving}
                            />
                        </S.FieldGroup>
                    </S.Section>

                    <S.Actions>
                        <S.SecondaryButton type="button" onClick={onClose} disabled={isSaving}>
                            {t('cancel', { lng: language, defaultValue: 'Cancel' })}
                        </S.SecondaryButton>
                        <S.PrimaryButton
                            type="submit"
                            disabled={!emailValid || !hasChanges || isSaving}
                        >
                            {isSaving
                                ? t('saving', { lng: language, defaultValue: 'Saving...' })
                                : t('saveChanges', {
                                      lng: language,
                                      defaultValue: 'Save changes',
                                  })}
                        </S.PrimaryButton>
                    </S.Actions>

                    {notification?.variant === 'success' && (
                        <S.Notification variant="success">
                            <S.SuccessText>{notification.message}</S.SuccessText>
                        </S.Notification>
                    )}

                    {notification?.variant === 'error' && (
                        <S.Notification variant="error">
                            <S.NotificationHeader>
                                <S.ErrorIcon />
                                <S.ErrorText>
                                    {t('errorTitle', {
                                        lng: language,
                                        defaultValue: 'Oops!',
                                    })}
                                </S.ErrorText>
                            </S.NotificationHeader>
                            <S.ErrorDescription>{notification.message}</S.ErrorDescription>
                        </S.Notification>
                    )}
                </S.Form>
            </L.ModalShell>
        </L.Backdrop>,
        document.body
    );
};

export default EditUserProfileModal;
